import type { PrefetchContext, PrefetchedDataProps } from '@change-corgi/core/react/prefetch';

import { useCurrentUserId } from 'src/app/shared/hooks/session';
import type { MergedState } from 'src/app/shared/hooks/state';
import { useMergedStates } from 'src/app/shared/hooks/state';

import { prefetchPetitionCommentsPageDataFromApi, usePetitionCommentsPageDataFromApi } from './pageData';
import {
	prefetchPetitionCommentsPagePaginatedDataFromApi,
	usePetitionCommentsPagePaginatedDataFromApi,
} from './pagePaginatedData';

export type PetitionCommentsPagePrefetchedContext = Readonly<{
	pageData: Parameters<typeof usePetitionCommentsPageDataFromApi>[0];
	pagePaginatedData: Parameters<typeof usePetitionCommentsPagePaginatedDataFromApi>[0];
}>;

export type PetitionCommentsPageCombinedState = MergedState<
	ReturnType<typeof usePetitionCommentsPageDataFromApi>,
	ReturnType<typeof usePetitionCommentsPagePaginatedDataFromApi>
>;

export function usePageContext(
	slug: string,
	prefetchedData?: PetitionCommentsPagePrefetchedContext,
): PetitionCommentsPageCombinedState {
	const userId = useCurrentUserId();
	return useMergedStates(
		usePetitionCommentsPageDataFromApi(prefetchedData?.pageData, slug, userId),
		usePetitionCommentsPagePaginatedDataFromApi(prefetchedData?.pagePaginatedData, slug),
	);
}

export async function prefetchPageContext(
	slug: string,
	context: PrefetchContext,
): Promise<PetitionCommentsPagePrefetchedContext> {
	const userId = context.session?.user?.id;
	const [pageData, pagePaginatedData] = await Promise.all([
		prefetchPetitionCommentsPageDataFromApi(context, slug, userId),
		prefetchPetitionCommentsPagePaginatedDataFromApi(context, slug),
	]);

	return { pageData, pagePaginatedData };
}

export type PetitionCommentsPagePrefetchedDataProps = PrefetchedDataProps<PetitionCommentsPagePrefetchedContext>;
